import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { 
  Package, Search, Plus, Edit2, Trash2, TrendingUp, Euro, 
  Percent, ShoppingBag, Briefcase, BarChart3, Clock, Box,
  Calculator, Tag
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatCurrency(amount) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
}

// Stats Cards
function StatsCards({ stats }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Total Prestations</CardDescription>
          <CardTitle className="text-2xl">{stats.total_prestations || 0}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 text-xs text-muted-foreground">
            <span>{stats.nb_services || 0} services</span>
            <span>·</span>
            <span>{stats.nb_produits || 0} produits</span>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Marge Moyenne</CardDescription>
          <CardTitle className="text-2xl text-emerald-600">{stats.marge_moyenne || 0}%</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">Sur l&apos;ensemble du catalogue</p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>CA Généré</CardDescription>
          <CardTitle className="text-2xl">{formatCurrency(stats.ca_total)}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">Depuis les factures</p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Top Prestation</CardDescription>
          <CardTitle className="text-lg truncate">
            {stats.top_prestations?.[0]?.nom || '-'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-emerald-600 font-medium">
            {stats.top_prestations?.[0] ? formatCurrency(stats.top_prestations[0].ca) : '-'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// Prestation Card
function PrestationCard({ prestation, onEdit, onDelete }) {
  var isService = prestation.categorie === 'service';
  var margeColor = prestation.marge_pct >= 30 ? 'text-emerald-600' : prestation.marge_pct >= 15 ? 'text-amber-600' : 'text-red-600';
  
  return (
    <Card className={'hover:shadow-md transition-shadow ' + (!prestation.actif ? 'opacity-60' : '')}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={'h-10 w-10 rounded-lg flex items-center justify-center ' + (isService ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600')}>
              {isService ? <Briefcase className="h-5 w-5" /> : <Box className="h-5 w-5" />}
            </div>
            <div>
              <h3 className="font-semibold text-sm">{prestation.nom}</h3>
              {prestation.description && (
                <p className="text-xs text-muted-foreground line-clamp-1">{prestation.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!prestation.actif && (
              <Badge variant="secondary" className="text-xs">Inactif</Badge>
            )}
            <Badge variant="outline" className="text-xs">
              {isService ? 'Service' : 'Produit'}
            </Badge>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-2 text-center py-3 bg-slate-50 rounded-lg mb-3">
          <div>
            <p className="text-xs text-muted-foreground">Prix HT</p>
            <p className="font-semibold text-sm">{formatCurrency(prestation.prix_vente_ht)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Coût</p>
            <p className="font-semibold text-sm">{formatCurrency(prestation.cout_revient)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Marge</p>
            <p className={'font-semibold text-sm ' + margeColor}>{prestation.marge_pct}%</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Tag className="h-3.5 w-3.5" />
            <span>{prestation.unite || 'unité'}</span>
            {prestation.taux_tva > 0 && (
              <>
                <span>·</span>
                <span>TVA {prestation.taux_tva}%</span>
              </>
            )}
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={function() { onEdit(prestation); }}>
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={function() { onDelete(prestation); }}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {prestation.nb_utilisations > 0 && (
          <div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
            Utilisé dans {prestation.nb_utilisations} facture(s)
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Prestation Form Modal
function PrestationFormModal({ prestation, isOpen, onClose, onSave, token }) {
  var [form, setForm] = useState({
    nom: '',
    description: '',
    categorie: 'service',
    unite: 'heure',
    prix_vente_ht: '',
    cout_revient: '',
    taux_tva: '0',
    actif: true
  });
  var [saving, setSaving] = useState(false);

  useEffect(function() {
    if (prestation) {
      setForm({
        nom: prestation.nom || '',
        description: prestation.description || '',
        categorie: prestation.categorie || 'service',
        unite: prestation.unite || 'heure',
        prix_vente_ht: String(prestation.prix_vente_ht || ''),
        cout_revient: String(prestation.cout_revient || ''),
        taux_tva: String(prestation.taux_tva || '0'),
        actif: prestation.actif !== false
      });
    } else {
      setForm({
        nom: '',
        description: '',
        categorie: 'service',
        unite: 'heure',
        prix_vente_ht: '',
        cout_revient: '',
        taux_tva: '0',
        actif: true
      });
    }
  }, [prestation]);

  var marge = 0;
  var prixHT = parseFloat(form.prix_vente_ht) || 0;
  var cout = parseFloat(form.cout_revient) || 0;
  if (prixHT > 0) {
    marge = ((prixHT - cout) / prixHT) * 100;
  }

  async function handleSubmit() {
    if (!form.nom.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    if (!form.prix_vente_ht || parseFloat(form.prix_vente_ht) <= 0) {
      toast.error('Le prix de vente est requis');
      return;
    }
    
    setSaving(true);
    try {
      var data = {
        ...form,
        prix_vente_ht: parseFloat(form.prix_vente_ht),
        cout_revient: parseFloat(form.cout_revient) || 0,
        taux_tva: parseFloat(form.taux_tva) || 0
      };
      
      if (prestation) {
        await axios.put(API_URL + '/api/prestations/' + prestation.id, data, {
          headers: { Authorization: 'Bearer ' + token }
        });
        toast.success('Prestation mise à jour');
      } else {
        await axios.post(API_URL + '/api/prestations', data, {
          headers: { Authorization: 'Bearer ' + token }
        });
        toast.success('Prestation créée');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{prestation ? 'Modifier la prestation' : 'Nouvelle prestation'}</DialogTitle>
          <DialogDescription>
            {prestation ? 'Modifiez les informations de votre prestation' : 'Ajoutez un service ou produit à votre catalogue'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label>Nom de la prestation *</Label>
              <Input 
                value={form.nom} 
                onChange={function(e) { setForm({...form, nom: e.target.value}); }} 
                placeholder="Ex: Développement web, Coaching..."
              />
            </div>
            
            <div className="space-y-2 col-span-2">
              <Label>Description</Label>
              <Input 
                value={form.description} 
                onChange={function(e) { setForm({...form, description: e.target.value}); }} 
                placeholder="Description courte (optionnel)"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select value={form.categorie} onValueChange={function(v) { setForm({...form, categorie: v}); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="produit">Produit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Unité de facturation</Label>
              <Select value={form.unite} onValueChange={function(v) { setForm({...form, unite: v}); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="heure">Heure</SelectItem>
                  <SelectItem value="jour">Jour</SelectItem>
                  <SelectItem value="forfait">Forfait</SelectItem>
                  <SelectItem value="unité">Unité</SelectItem>
                  <SelectItem value="mois">Mois</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Prix de vente HT *</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.prix_vente_ht} 
                  onChange={function(e) { setForm({...form, prix_vente_ht: e.target.value}); }} 
                  placeholder="0.00"
                  className="pl-9"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Coût de revient</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.cout_revient} 
                  onChange={function(e) { setForm({...form, cout_revient: e.target.value}); }} 
                  placeholder="0.00"
                  className="pl-9"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>TVA</Label>
              <Select value={form.taux_tva} onValueChange={function(v) { setForm({...form, taux_tva: v}); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% (Auto-entrepreneur)</SelectItem>
                  <SelectItem value="5.5">5.5%</SelectItem>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="20">20%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Marge calculée */}
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Marge calculée</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm">
                    {formatCurrency(prixHT - cout)} / unité
                  </span>
                  <span className={'text-lg font-bold ' + (marge >= 30 ? 'text-emerald-600' : marge >= 15 ? 'text-amber-600' : 'text-red-600')}>
                    {marge.toFixed(1)}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="flex items-center justify-between pt-2">
            <div className="space-y-0.5">
              <Label>Prestation active</Label>
              <p className="text-xs text-muted-foreground">Visible dans les devis et factures</p>
            </div>
            <Switch 
              checked={form.actif} 
              onCheckedChange={function(v) { setForm({...form, actif: v}); }}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {prestation ? 'Enregistrer' : 'Créer la prestation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Catalogue Page
function CataloguePage() {
  var [loading, setLoading] = useState(true);
  var [stats, setStats] = useState({});
  var [prestations, setPrestations] = useState([]);
  var [search, setSearch] = useState('');
  var [categorieFilter, setCategorieFilter] = useState('all');
  var [showForm, setShowForm] = useState(false);
  var [editingPrestation, setEditingPrestation] = useState(null);
  var [deleteConfirm, setDeleteConfirm] = useState(null);

  var token = localStorage.getItem('token');

  var fetchData = useCallback(async function() {
    try {
      var [statsRes, prestationsRes] = await Promise.all([
        axios.get(API_URL + '/api/prestations-stats', { headers: { Authorization: 'Bearer ' + token } }),
        axios.get(API_URL + '/api/prestations', { headers: { Authorization: 'Bearer ' + token } })
      ]);
      setStats(statsRes.data);
      setPrestations(prestationsRes.data.prestations || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(function() {
    fetchData();
  }, [fetchData]);

  function handleEditPrestation(prestation) {
    setEditingPrestation(prestation);
    setShowForm(true);
  }

  async function handleDeletePrestation() {
    if (!deleteConfirm) return;
    try {
      await axios.delete(API_URL + '/api/prestations/' + deleteConfirm.id, {
        headers: { Authorization: 'Bearer ' + token }
      });
      toast.success('Prestation supprimée');
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  }

  var filteredPrestations = prestations.filter(function(p) {
    var matchSearch = !search || 
      (p.nom || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.description || '').toLowerCase().includes(search.toLowerCase());
    var matchCategorie = categorieFilter === 'all' || p.categorie === categorieFilter;
    return matchSearch && matchCategorie;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="catalogue-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Catalogue Prestations</h1>
          <p className="text-muted-foreground">Gérez vos services et produits avec leurs tarifs</p>
        </div>
        <Button onClick={function() { setEditingPrestation(null); setShowForm(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Nouvelle prestation
        </Button>
      </div>

      {/* Stats */}
      <StatsCards stats={stats} />

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Rechercher une prestation..." 
            value={search}
            onChange={function(e) { setSearch(e.target.value); }}
            className="pl-9"
          />
        </div>
        <Select value={categorieFilter} onValueChange={setCategorieFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Catégorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="service">Services</SelectItem>
            <SelectItem value="produit">Produits</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Prestations Grid */}
      {filteredPrestations.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPrestations.map(function(prestation) {
            return (
              <PrestationCard
                key={prestation.id}
                prestation={prestation}
                onEdit={handleEditPrestation}
                onDelete={function() { setDeleteConfirm(prestation); }}
              />
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-lg font-semibold mb-2">Aucune prestation</h3>
            <p className="text-muted-foreground mb-4">
              {search ? 'Aucun résultat pour cette recherche' : 'Commencez par ajouter vos services ou produits'}
            </p>
            {!search && (
              <Button onClick={function() { setEditingPrestation(null); setShowForm(true); }}>
                <Plus className="h-4 w-4 mr-2" /> Ajouter une prestation
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Top Prestations */}
      {stats.top_prestations && stats.top_prestations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Top Prestations par CA
            </CardTitle>
            <CardDescription>Vos meilleures ventes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.top_prestations.map(function(p, idx) {
                return (
                  <div key={p.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{p.nom}</p>
                      <p className="text-xs text-muted-foreground">{p.quantite} vendus · Marge {p.marge_pct}%</p>
                    </div>
                    <p className="font-semibold text-emerald-600">{formatCurrency(p.ca)}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      <PrestationFormModal
        prestation={editingPrestation}
        isOpen={showForm}
        onClose={function() { setShowForm(false); setEditingPrestation(null); }}
        onSave={fetchData}
        token={token}
      />

      <AlertDialog open={!!deleteConfirm} onOpenChange={function() { setDeleteConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette prestation ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La prestation &quot;{deleteConfirm?.nom}&quot; sera supprimée de votre catalogue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePrestation} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default CataloguePage;
